import React, { useState } from 'react';
import { motion } from 'framer-motion';
import Navigation from '../../components/Navigation';
import CosmicBackground from '../../components/CosmicBackground';
import ToolInterface from '../../components/ToolInterface';
import { getToolById } from '../../data/toolsData';

const BlogPostGeneratorPage = () => {
  const tool = getToolById('blog-post-generator');
  const [topic, setTopic] = useState('');
  const [keywords, setKeywords] = useState('');
  const [audience, setAudience] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [result, setResult] = useState('');

  const handleGenerate = async () => {
    if (!topic.trim()) return;
    
    setIsGenerating(true);
    
    // Simulate AI generation
    setTimeout(() => {
      const generatedContent = `# ${topic}

## Introduction

In today's digital landscape, ${topic.toLowerCase()} has become increasingly important for ${audience || 'businesses and individuals'}. This comprehensive guide will walk you through everything you need to know about ${topic.toLowerCase()}.

## Key Points

${keywords.split(',').map(keyword => `- **${keyword.trim()}**: Understanding the importance of ${keyword.trim()} in ${topic.toLowerCase()}`).join('\n')}

## Main Content

### Understanding the Basics

When it comes to ${topic.toLowerCase()}, there are several fundamental concepts that ${audience || 'everyone'} should understand. These principles form the foundation of successful implementation.

### Best Practices

1. **Research thoroughly**: Always start with comprehensive research
2. **Plan strategically**: Develop a clear action plan
3. **Execute consistently**: Maintain regular effort and monitoring
4. **Measure results**: Track progress and adjust as needed

### Advanced Strategies

For those looking to take their ${topic.toLowerCase()} efforts to the next level, consider implementing these advanced strategies:

- Leverage data analytics for deeper insights
- Implement automation where possible
- Focus on user experience optimization
- Stay updated with industry trends

## Conclusion

${topic} represents a significant opportunity for ${audience || 'growth and success'}. By implementing the strategies outlined in this guide, you'll be well-positioned to achieve your goals.

Remember to stay consistent, measure your progress, and continuously adapt your approach based on the results you're seeing.

---

*This blog post was generated using AI-powered content creation tools. Always review and customize the content to match your brand voice and specific requirements.*`;

      setResult(generatedContent);
      setIsGenerating(false);
    }, 2000);
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(result);
  };

  const handleDownload = () => {
    const blob = new Blob([result], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${topic.replace(/\s+/g, '-').toLowerCase()}-blog-post.md`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="min-h-screen bg-gray-900 text-white relative overflow-hidden">
      <CosmicBackground />
      <Navigation />
      
      <section className="pt-32 pb-20 relative z-10">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <ToolInterface
              tool={tool}
              onGenerate={handleGenerate}
              isGenerating={isGenerating}
              result={result}
              onCopy={handleCopy}
              onDownload={handleDownload}
            >
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-poppins font-medium text-gray-300 mb-2">
                    Blog Topic *
                  </label>
                  <input
                    type="text"
                    value={topic}
                    onChange={(e) => setTopic(e.target.value)}
                    placeholder="e.g., How to Build a Successful Online Business"
                    className="w-full bg-gray-700/50 border border-gray-600/50 rounded-lg px-4 py-3 text-white font-poppins focus:border-green-500 focus:outline-none"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-poppins font-medium text-gray-300 mb-2">
                    Keywords (comma-separated)
                  </label>
                  <input
                    type="text"
                    value={keywords}
                    onChange={(e) => setKeywords(e.target.value)}
                    placeholder="e.g., entrepreneurship, digital marketing, passive income"
                    className="w-full bg-gray-700/50 border border-gray-600/50 rounded-lg px-4 py-3 text-white font-poppins focus:border-green-500 focus:outline-none"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-poppins font-medium text-gray-300 mb-2">
                    Target Audience
                  </label>
                  <input
                    type="text"
                    value={audience}
                    onChange={(e) => setAudience(e.target.value)}
                    placeholder="e.g., aspiring entrepreneurs, small business owners"
                    className="w-full bg-gray-700/50 border border-gray-600/50 rounded-lg px-4 py-3 text-white font-poppins focus:border-green-500 focus:outline-none"
                  />
                </div>
              </div>
            </ToolInterface>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default BlogPostGeneratorPage;